import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Business as BusinessIcon,
  Language as LanguageIcon,
  People as PeopleIcon,
  Facebook as FacebookIcon
} from '@mui/icons-material';
import { brandService } from '../../services/brandService';
import api from '../../config/api';

const BrandManagement = () => {
  const navigate = useNavigate();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentBrand, setCurrentBrand] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    logoUrl: '',
    acquisitionDate: ''
  });
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedBrandId, setSelectedBrandId] = useState(null);
  // Brand logo upload — uploaded file is saved to uploads/brands/{id}/logo.{ext}
  // server-side and rendered on every PO PDF as the brand mark. Strict source:
  // PO PDF uses this file or no logo (no fallback to subsidiary letterhead).
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoError, setLogoError] = useState('');
  const [logoCacheBust, setLogoCacheBust] = useState(0);
  
  // Fetch brands on component mount
  useEffect(() => {
    fetchBrands();
  }, []);
  
  const fetchBrands = async () => {
    try {
      setLoading(true);
      setError('');
      
      const brandsData = await brandService.getBrands();
      setBrands(brandsData);
      
    } catch (err) {
      console.error('Error fetching brands:', err);
      if (err.response?.status === 401) {
        setError('Authentication failed. Please login again.');
      } else if (err.response?.status === 403) {
        setError('You do not have permission to view brands.');
      } else if (err.response?.status >= 500) {
        setError('Server error. Please try again later.');
      } else {
        setError(err.response?.data?.message || 'Failed to load brands. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Upload a logo file for the brand currently being edited. POSTs to
  // /api/brands/:id/logo (multipart, field name "file"). On success the
  // server stores the file as uploads/brands/{id}/logo.{ext} and sets
  // brands_v2.logo_url to /api/brands/:id/logo. We refresh the list and
  // bump the cache-bust counter so the preview image re-fetches.
  const handleLogoFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !currentBrand?.id) return;
    setLogoError('');
    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`/api/brands/${currentBrand.id}/logo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const urlPath = res?.data?.url || `/api/brands/${currentBrand.id}/logo`;
      setFormData(prev => ({ ...prev, logoUrl: urlPath }));
      setLogoCacheBust(c => c + 1);
      await fetchBrands();
    } catch (err) {
      console.error('Brand logo upload failed', err);
      setLogoError(err?.response?.data?.error || err.message || 'Upload failed');
    } finally {
      setLogoUploading(false);
      // Clear the file input value so re-uploading the same file fires onChange again.
      if (e.target) e.target.value = '';
    }
  };

  const handleLogoDelete = async () => {
    if (!currentBrand?.id) return;
    setLogoError('');
    setLogoUploading(true);
    try {
      await api.delete(`/api/brands/${currentBrand.id}/logo`);
      setFormData(prev => ({ ...prev, logoUrl: '' }));
      setLogoCacheBust(c => c + 1);
      await fetchBrands();
    } catch (err) {
      console.error('Brand logo delete failed', err);
      setLogoError(err?.response?.data?.error || err.message || 'Delete failed');
    } finally {
      setLogoUploading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleOpenDialog = (brand = null) => {
    if (brand) {
      setCurrentBrand(brand);
      setFormData({
        name: brand.name,
        description: brand.description,
        logoUrl: brand.logo_url || brand.logoUrl,
        acquisitionDate: brand.acquisition_date || brand.acquisitionDate || ''
      });
      setIsEditing(true);
    } else {
      setCurrentBrand(null);
      setFormData({
        name: '',
        description: '',
        logoUrl: '',
        acquisitionDate: ''
      });
      setIsEditing(false);
    }
    setError(''); setOpenDialog(true);
  };
  
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentBrand(null);
  };
  
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    // Double-submit guard: creation runs for seconds server-side (database build);
    // a second click in that window used to mint a full duplicate brand (L'AVANT
    // incident 2026-07-21). Backend also 409s, this keeps the UI honest.
    if (submitting) return;
    setSubmitting(true);
    try {
      setError('');

      if (isEditing) {
        // Update existing brand
        const updatedBrand = await brandService.updateBrand(currentBrand.id, formData);
        setBrands(brands.map(brand => 
          brand.id === currentBrand.id ? updatedBrand : brand
        ));
      } else {
        // Create new brand
        const newBrand = await brandService.createBrand(formData);
        setBrands([...brands, newBrand]);
      }
      
      handleCloseDialog();
    } catch (err) {
      console.error('Error saving brand:', err);
      if (err.response?.status === 400) {
        setError(err.response?.data?.message || 'Invalid brand data. Please check your input.');
      } else if (err.response?.status === 403) {
        setError('You do not have permission to save brands.');
      } else {
        setError(err.response?.data?.message || 'Failed to save brand. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleDeleteBrand = async (brandId) => {
    try {
      setError('');
      await brandService.deleteBrand(brandId);
      setBrands(brands.filter(brand => brand.id !== brandId));
      handleMenuClose();
    } catch (err) {
      console.error('Error deleting brand:', err);
      if (err.response?.status === 403) {
        setError('You do not have permission to delete this brand.');
      } else if (err.response?.status === 404) {
        setError('Brand not found.');
      } else {
        setError(err.response?.data?.message || 'Failed to delete brand. Please try again.');
      }
      handleMenuClose();
    }
  };
  
  const handleMenuOpen = (event, brandId) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedBrandId(brandId);
  };
  
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedBrandId(null);
  };
  
  const handleEditFromMenu = () => {
    const brand = brands.find(b => b.id === selectedBrandId);
    if (brand) {
      handleOpenDialog(brand);
    }
    handleMenuClose();
  };
  
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  // Generate a brand logo placeholder with initials
  const getBrandInitials = (name) => {
    if (!name) return 'B';
    const words = name.split(' ');
    if (words.length >= 2) {
      return `${words[0][0]}${words[1][0]}`;
    }
    return name[0];
  };

  // Deterministic accent palette per brand — used for both placeholder and the soft hero tint.
  const ACCENT_PALETTE = [
    { solid: '#6366F1', soft: 'linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%)' }, // indigo
    { solid: '#0EA5E9', soft: 'linear-gradient(135deg, #ECFEFF 0%, #CFFAFE 100%)' }, // sky
    { solid: '#F97316', soft: 'linear-gradient(135deg, #FFF7ED 0%, #FFEDD5 100%)' }, // orange
    { solid: '#EAB308', soft: 'linear-gradient(135deg, #FEFCE8 0%, #FEF3C7 100%)' }, // amber
    { solid: '#8B5CF6', soft: 'linear-gradient(135deg, #FAF5FF 0%, #EDE9FE 100%)' }, // violet
    { solid: '#EF4444', soft: 'linear-gradient(135deg, #FEF2F2 0%, #FEE2E2 100%)' }, // rose
    { solid: '#10B981', soft: 'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)' }, // emerald
    { solid: '#EC4899', soft: 'linear-gradient(135deg, #FDF2F8 0%, #FCE7F3 100%)' }, // pink
  ];

  const getAccent = (id) => {
    const i = Math.abs(parseInt(id, 10) || 0) % ACCENT_PALETTE.length;
    return ACCENT_PALETTE[i];
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ mb: 0.5, fontWeight: 700, letterSpacing: '-0.02em' }}>
            Brand Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create and manage your brands
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          disableElevation
          sx={{
            borderRadius: 2,
            px: 2.5,
            py: 1,
            textTransform: 'none',
            fontWeight: 600,
            background: 'linear-gradient(45deg, var(--color-primary) 30%, var(--color-accent) 90%)',
            boxShadow: '0 6px 16px rgba(99, 102, 241, 0.25)',
            '&:hover': { boxShadow: '0 8px 20px rgba(99, 102, 241, 0.32)' },
          }}
        >
          Add Brand
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : brands.length === 0 ? (
        <Box
          sx={{
            borderRadius: 3,
            border: '2px dashed',
            borderColor: 'divider',
            p: 6,
            textAlign: 'center',
            background: 'linear-gradient(135deg, rgba(99,102,241,0.03) 0%, rgba(14,165,233,0.03) 100%)',
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              bgcolor: 'rgba(99, 102, 241, 0.08)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
            }}
          >
            <BusinessIcon sx={{ fontSize: 32, color: 'var(--color-primary)' }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
            No brands yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create your first brand to start aggregating data
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            disableElevation
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              background: 'linear-gradient(45deg, var(--color-primary) 30%, var(--color-accent) 90%)',
            }}
          >
            Add Brand
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {brands.map(brand => {
            const accent = getAccent(brand.id);
            const logoUrl = brand.logo_url || brand.logoUrl;
            const acquired = brand.acquisition_date || brand.acquisitionDate;
            const created = brand.created_at || brand.createdAt;
            const metaLine = acquired
              ? `Acquired ${formatDate(acquired)}`
              : created ? `Added ${formatDate(created)}` : '';

            return (
              <Grid item xs={12} sm={6} md={4} key={brand.id}>
                <Card
                  sx={{
                    borderRadius: 3,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    border: '1px solid',
                    borderColor: 'rgba(15, 23, 42, 0.06)',
                    boxShadow: '0 2px 8px rgba(15, 23, 42, 0.04)',
                    overflow: 'hidden',
                    transition: 'transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease',
                    '&:hover': {
                      transform: 'translateY(-4px) scale(1.005)',
                      boxShadow: '0 16px 32px rgba(15, 23, 42, 0.10)',
                      borderColor: 'rgba(15, 23, 42, 0.10)',
                    },
                  }}
                >
                  {/* Logo hero — soft tinted backdrop, logo contained (never cropped) */}
                  <Box
                    sx={{
                      position: 'relative',
                      height: 180,
                      background: accent.soft,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: 3,
                    }}
                  >
                    {logoUrl ? (
                      <Box
                        component="img"
                        src={logoUrl}
                        alt={brand.name}
                        sx={{
                          maxHeight: '100%',
                          maxWidth: '100%',
                          objectFit: 'contain',
                          filter: 'drop-shadow(0 2px 6px rgba(15,23,42,0.06))',
                        }}
                      />
                    ) : (
                      <Avatar
                        sx={{
                          width: 88,
                          height: 88,
                          bgcolor: accent.solid,
                          fontSize: '2rem',
                          fontWeight: 700,
                          boxShadow: '0 8px 20px rgba(15,23,42,0.12)',
                        }}
                      >
                        {getBrandInitials(brand.name)}
                      </Avatar>
                    )}
                    <IconButton
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 10,
                        right: 10,
                        bgcolor: 'rgba(255,255,255,0.85)',
                        backdropFilter: 'blur(8px)',
                        boxShadow: '0 2px 6px rgba(15,23,42,0.08)',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.95)' },
                      }}
                      onClick={(e) => handleMenuOpen(e, brand.id)}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  <CardContent sx={{ flexGrow: 1, px: 2.5, pt: 2.5, pb: 1.5 }}>
                    <Typography
                      variant="h5"
                      component="div"
                      sx={{ fontWeight: 700, letterSpacing: '-0.01em', mb: 0.75 }}
                    >
                      {brand.name}
                    </Typography>
                    {brand.description && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 1.5,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          minHeight: 40,
                          lineHeight: 1.5,
                        }}
                      >
                        {brand.description}
                      </Typography>
                    )}
                    {metaLine && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.disabled',
                          textTransform: 'uppercase',
                          letterSpacing: '0.06em',
                          fontWeight: 600,
                          fontSize: '0.7rem',
                        }}
                      >
                        {metaLine}
                      </Typography>
                    )}
                  </CardContent>

                  <Divider sx={{ borderColor: 'rgba(15, 23, 42, 0.06)' }} />

                  <CardActions sx={{ p: 1.5, gap: 0.5 }}>
                    <Button
                      size="small"
                      startIcon={<PeopleIcon sx={{ fontSize: 16 }} />}
                      onClick={() => navigate(`/admin?tab=users`)}
                      sx={{
                        flex: 1,
                        borderRadius: 1.5,
                        textTransform: 'none',
                        fontWeight: 500,
                        color: 'text.secondary',
                        '&:hover': { bgcolor: 'rgba(15,23,42,0.04)', color: 'text.primary' },
                      }}
                    >
                      Users
                    </Button>
                    <Button
                      size="small"
                      startIcon={<LanguageIcon sx={{ fontSize: 16 }} />}
                      onClick={() => navigate(`/connectors?brandId=${brand.id}`)}
                      sx={{
                        flex: 1,
                        borderRadius: 1.5,
                        textTransform: 'none',
                        fontWeight: 500,
                        color: 'var(--color-primary)',
                        '&:hover': { bgcolor: 'rgba(99,102,241,0.08)' },
                      }}
                    >
                      Connectors
                    </Button>
                    <Button
                      size="small"
                      startIcon={<FacebookIcon sx={{ fontSize: 16 }} />}
                      onClick={() => navigate(`/connectors/facebook/manage?brand_id=${brand.id}`)}
                      sx={{
                        flex: 1,
                        borderRadius: 1.5,
                        textTransform: 'none',
                        fontWeight: 500,
                        color: '#1877F2',
                        '&:hover': { bgcolor: 'rgba(24,119,242,0.08)' },
                      }}
                    >
                      FB Pages
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}

          {/* Add-brand placeholder tile — last cell */}
          <Grid item xs={12} sm={6} md={4}>
            <Box
              role="button"
              tabIndex={0}
              onClick={() => handleOpenDialog()}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleOpenDialog(); }}
              sx={{
                height: '100%',
                minHeight: 320,
                borderRadius: 3,
                border: '2px dashed',
                borderColor: 'rgba(15, 23, 42, 0.12)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                background: 'linear-gradient(135deg, rgba(99,102,241,0.02) 0%, rgba(14,165,233,0.02) 100%)',
                '&:hover': {
                  borderColor: 'var(--color-primary)',
                  background: 'linear-gradient(135deg, rgba(99,102,241,0.05) 0%, rgba(14,165,233,0.05) 100%)',
                  transform: 'translateY(-2px)',
                },
                '&:focus-visible': {
                  outline: '2px solid var(--color-primary)',
                  outlineOffset: '2px',
                },
              }}
            >
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  bgcolor: 'rgba(99, 102, 241, 0.10)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 1.5,
                }}
              >
                <AddIcon sx={{ fontSize: 28, color: 'var(--color-primary)' }} />
              </Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.25 }}>
                Add Brand
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Bring another brand into your workspace
              </Typography>
            </Box>
          </Grid>
        </Grid>
      )}
      
      {/* Brand Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          {isEditing ? 'Edit Brand' : 'Create New Brand'}
        </DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          <Box sx={{ my: 2 }}>
            <TextField
              fullWidth
              label="Brand Name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              margin="normal"
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
            <TextField
              fullWidth
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              margin="normal"
              multiline
              rows={3}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
            <TextField
              fullWidth
              label="Logo URL (optional)"
              name="logoUrl"
              value={formData.logoUrl}
              onChange={handleInputChange}
              margin="normal"
              helperText="Enter an external URL, or upload a file below for use on Purchase Order PDFs."
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
            {/* Brand logo upload — file source for PO PDF brand mark. Only available
                when editing an existing brand (file is saved keyed by brand id). */}
            {isEditing && currentBrand?.id ? (
              <Box sx={{ mt: 1.5, mb: 1, p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Brand logo on Purchase Orders</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                  Renders on every PO PDF for this brand. PNG / JPG / WEBP / SVG, up to 2 MB.
                  If you don't upload a file, POs for this brand render without a logo
                  (no fallback to the subsidiary letterhead).
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar
                    variant="rounded"
                    src={formData.logoUrl ? `${formData.logoUrl}${formData.logoUrl.startsWith('/api/') ? `?cb=${logoCacheBust}` : ''}` : undefined}
                    sx={{ width: 96, height: 56, bgcolor: 'background.default', border: '1px solid', borderColor: 'divider' }}
                  >
                    <BusinessIcon />
                  </Avatar>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Button
                      component="label"
                      variant="outlined"
                      size="small"
                      disabled={logoUploading}
                      sx={{ borderRadius: 2 }}
                    >
                      {logoUploading ? 'Uploading…' : 'Upload logo file'}
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                        hidden
                        onChange={handleLogoFileChange}
                      />
                    </Button>
                    {formData.logoUrl && String(formData.logoUrl).startsWith('/api/') && (
                      <Button
                        size="small"
                        color="error"
                        onClick={handleLogoDelete}
                        disabled={logoUploading}
                        sx={{ alignSelf: 'flex-start' }}
                      >
                        Remove uploaded file
                      </Button>
                    )}
                  </Box>
                </Box>
                {logoError && <Alert severity="error" sx={{ mt: 1.5 }}>{logoError}</Alert>}
              </Box>
            ) : (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, mb: 1, fontStyle: 'italic' }}>
                Save the brand first to enable logo file upload.
              </Typography>
            )}
            <TextField
              fullWidth
              label="Acquisition Date (optional)"
              name="acquisitionDate"
              type="date"
              value={formData.acquisitionDate}
              onChange={handleInputChange}
              margin="normal"
              InputLabelProps={{
                shrink: true,
              }}
              helperText="Date when the brand was acquired"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseDialog} sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting}
            sx={{
              borderRadius: 2,
              background: 'linear-gradient(45deg, var(--color-primary) 30%, var(--color-accent) 90%)',
            }}
          >
            {submitting ? 'Saving...' : (isEditing ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Menu for Brand Options */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 3,
          sx: {
            borderRadius: 2,
            minWidth: 180,
            mt: 1,
          },
        }}
      >
        <MenuItem onClick={handleEditFromMenu}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleDeleteBrand(selectedBrandId)}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText sx={{ color: 'error.main' }}>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default BrandManagement;